import T from 'prop-types';
import React from 'react';
import moment from 'moment';
import { Box, Loader, Text } from '@acast/decibel';
import ProgressBar from 'shared/components/progress-bar';

const ONE_SECOND = 1000;
const ONE_MINUTE = ONE_SECOND * 60;
const ONE_HOUR = ONE_MINUTE * 60;
const ENCODED_PER_HOUR = ONE_MINUTE + ONE_MINUTE * 0.2;
const THRESHOLD = 0.5;
const HUNDRED_PERCENT_PROGRESS = 1;

const calculateLongestReasonableEncodingTime = durationMilliseconds => {
  const assumedTime = (durationMilliseconds / ONE_HOUR) * ENCODED_PER_HOUR;
  const threshold = assumedTime * THRESHOLD;

  return assumedTime + threshold;
};

const getTimeSince = start => {
  const time = moment()
    .subtract(moment(start).unix(), 'seconds')
    .valueOf();

  return time < 0 ? 0 : time;
};

const withProgress = Component =>
  class extends React.Component {
    constructor(props, ...rest) {
      super(props, ...rest);

      this.assumedEncodingTime = calculateLongestReasonableEncodingTime(
        props.duration * ONE_SECOND
      );
      this.state = {
        progress: 0,
        isOverTime: false,
      };
    }

    static propTypes = {
      duration: T.number.isRequired,
      start: T.string,
    };

    UNSAFE_componentWillMount() {
      this.update(this.props.start);
    }

    componentWillUnmount() {
      this.stopTimer();
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
      if (this.props.start !== nextProps.start) {
        this.update(nextProps.start);
      }
    }

    update(start) {
      if (start) {
        const progress = getTimeSince(start) / this.assumedEncodingTime;
        this.setState({ progress }, () => {
          this.startTimer();
        });
      } else {
        this.startTimer();
      }
    }

    startTimer() {
      this.stopTimer();

      const { progress } = this.state;
      const shouldBeCompleted = progress > HUNDRED_PERCENT_PROGRESS;
      if (shouldBeCompleted) {
        this.setState(
          {
            isOverTime: true,
          },
          () => {
            this.stopTimer();
          }
        );
      } else {
        const timeout = this.assumedEncodingTime / 100;

        this.setState(
          {
            progress: progress + 0.01,
          },
          () => {
            this.timer = setTimeout(() => this.startTimer(), timeout);
          }
        );
      }
    }

    stopTimer() {
      if (this.timer) {
        clearTimeout(this.timer);
        this.timer = undefined;
      }
    }

    render() {
      const { isOverTime, progress } = this.state;
      const percent = Math.floor(progress * 100);

      if (isOverTime) {
        return (
          <Box flexDirection="column" alignItems="center" gap={4}>
            <Loader />
            <Text variant="caption">
              The encoding is taking longer than expected. Please be patient.
            </Text>
          </Box>
        );
      }

      return <Component {...this.props} percent={percent} />;
    }
  };

const Progress = ({ fileName, percent }) => (
  <div className="encoding-in-progress-pane">
    <span className="encoding-in-progress-pane__label">
      Encoding file: "{fileName}" ({percent}%)
    </span>
    <Box width="60%" mx="auto" mt={4} flexDirection="column" alignItems="center">
      <ProgressBar value={percent} />
    </Box>
  </div>
);

Progress.propTypes = {
  fileName: T.string.isRequired,
  percent: T.number.isRequired,
};

export default withProgress(Progress);
